import React from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '../Common/input';
import { cn } from '../../lib/utils';
import { ANALYSIS_LIMIT_KEYS, type AnalysisLimits } from '../../types/projectAnalysis';

/** Draft values as typed (empty = inherit). */
export type LimitsDraft = Record<string, string>;

/** Known keys first (in a stable order), then any key the backend added since. */
export const limitKeys = (limits: AnalysisLimits): string[] => {
  const known = ANALYSIS_LIMIT_KEYS.filter((k) => k in limits.defaults || k in limits.bounds);
  const extra = Object.keys(limits.defaults).filter((k) => !(ANALYSIS_LIMIT_KEYS as readonly string[]).includes(k));
  return [...known, ...extra];
};

export const formatLimit = (value: number | undefined, locale: string): string =>
  value === undefined ? '—' : new Intl.NumberFormat(locale.startsWith('en') ? 'en-GB' : 'fr-FR').format(value);

export const boundsOf = (limits: AnalysisLimits, key: string): [number, number] | null => {
  const b = limits.bounds[key];
  return Array.isArray(b) && b.length === 2 ? [Number(b[0]), Number(b[1])] : null;
};

export const draftFrom = (values: Record<string, number>): LimitsDraft =>
  Object.fromEntries(Object.entries(values).map(([k, v]) => [k, String(v)]));

/**
 * Typed values -> payload, with the keys whose value is not an integer within
 * its bounds.
 */
export function parseDraft(
  draft: LimitsDraft,
  limits: AnalysisLimits
): { values: Record<string, number>; errors: Record<string, true> } {
  const values: Record<string, number> = {};
  const errors: Record<string, true> = {};
  for (const [key, raw] of Object.entries(draft)) {
    const text = raw.trim();
    if (!text) continue;
    const n = Number(text);
    const bounds = boundsOf(limits, key);
    if (!Number.isInteger(n) || (bounds && (n < bounds[0] || n > bounds[1]))) errors[key] = true;
    else values[key] = n;
  }
  return { values, errors };
}

interface AnalysisLimitsTableProps {
  limits: AnalysisLimits;
  /** Value used when the field is empty (code default, or the global value for an org). */
  inheritedOf: (key: string) => number | undefined;
  inheritedLabel: string;
  /** Absent: read-only (inherited and effective values only). */
  editor?: {
    draft: LimitsDraft;
    onChange: (draft: LimitsDraft) => void;
    errors: Record<string, true>;
    disabled?: boolean;
  };
}

/** One row per limit: label and help, the editable value, the inherited and effective values. */
export const AnalysisLimitsTable: React.FC<AnalysisLimitsTableProps> = ({
  limits,
  inheritedOf,
  inheritedLabel,
  editor,
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="py-2 pr-4 font-medium">{t('projectAnalysisLimits.columns.limit')}</th>
            {editor && <th className="py-2 pr-4 font-medium">{t('projectAnalysisLimits.columns.value')}</th>}
            <th className="py-2 pr-4 font-medium">{inheritedLabel}</th>
            <th className="py-2 pr-4 font-medium">{t('projectAnalysisLimits.columns.effective')}</th>
          </tr>
        </thead>
        <tbody>
          {limitKeys(limits).map((key) => {
            const bounds = boundsOf(limits, key);
            const inherited = inheritedOf(key);
            const id = `analysis-limit-${key}`;
            const invalid = !!editor?.errors[key];
            return (
              <tr key={key} className="border-b border-border align-top last:border-0">
                <td className="py-3 pr-4">
                  <label htmlFor={editor ? id : undefined} className="font-medium text-foreground">
                    {t(`projectAnalysisLimits.keys.${key}.label`, { defaultValue: key })}
                  </label>
                  <p className="mt-0.5 max-w-md text-xs text-muted-foreground">
                    {t(`projectAnalysisLimits.keys.${key}.help`, { defaultValue: '' })}
                  </p>
                </td>
                {editor && (
                  <td className="py-3 pr-4">
                    <Input
                      id={id}
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={bounds?.[0]}
                      max={bounds?.[1]}
                      value={editor.draft[key] ?? ''}
                      onChange={(e) => editor.onChange({ ...editor.draft, [key]: e.target.value })}
                      placeholder={
                        inherited !== undefined
                          ? t('projectAnalysisLimits.placeholder', { value: formatLimit(inherited, i18n.language) })
                          : undefined
                      }
                      disabled={editor.disabled}
                      aria-invalid={invalid}
                      className={cn('h-9 w-44', invalid && 'border-destructive focus-visible:ring-destructive')}
                    />
                    {bounds && (
                      <p className={cn('mt-1 text-[11px]', invalid ? 'text-destructive' : 'text-muted-foreground')}>
                        {t('projectAnalysisLimits.bounds', {
                          min: formatLimit(bounds[0], i18n.language),
                          max: formatLimit(bounds[1], i18n.language),
                        })}
                      </p>
                    )}
                  </td>
                )}
                <td className="py-3 pr-4 tabular-nums text-muted-foreground">{formatLimit(inherited, i18n.language)}</td>
                <td className="py-3 pr-4 font-medium tabular-nums text-foreground">
                  {formatLimit(limits.effective[key], i18n.language)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AnalysisLimitsTable;
